import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiClipboard, FiClock, FiAward, FiCheckCircle, FiDollarSign, FiTrendingUp,
} from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import { apiRequest } from '../utils/apiRequest';
import { API_URLS } from '../config';
import Select from 'react-select';
import FilterPanel, { FilterField, filterSelectStyles } from '../Components/FilterPanel';
import './Home.css';

const currentMonthKey = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
};

const formatINR = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Home's period/company/coordinator filters, kept across a visit to a
// drill-down dashboard view and back (sessionStorage — same tab/session only,
// so a stale filter never lingers across a fresh login on a shared machine).
const HOME_FILTERS_KEY = 'home_filters_v1';
const loadSavedHomeFilters = () => {
  try {
    const raw = sessionStorage.getItem(HOME_FILTERS_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
};

const formatMonthLabel = (ym) => {
  if (!/^\d{4}-\d{2}$/.test(ym || '')) return '';
  const [y, m] = ym.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};
const SimpleIncentiveView = ({ data, viewDetailsUrl }) => (
  data.enabled === false ? (
    <p className="home-muted">The coordinator incentive program is not enabled for your company.</p>
  ) : (
    <div className="home-self-card">
      <div className="home-self-total">{formatINR(data.total)}</div>
      <div className="home-self-sub">{formatMonthLabel(data.month)}</div>
      {viewDetailsUrl && (
        <a href={viewDetailsUrl} className="home-link" style={{ marginTop: 6, fontSize: 13 }}>
          View details →
        </a>
      )}
    </div>
  )
);


const HomeSummaryCards = ({ counts, eligibleTotal, totalIncentive }) => {
  return (
    <div className="home-summary-row">
      <QCard icon={<FiClipboard size={14} />}    label="Total enquiries"    value={counts.total}              accent="#6366f1" />
      <QCard icon={<FiClock size={14} />}         label="In process"         value={counts.inProcess}          accent="#f97316" />
      <QCard icon={<FiAward size={14} />}         label="Won"                value={counts.won}                accent="#22c55e" />
      <QCard icon={<FiCheckCircle size={14} />}   label="Probable incentive" value={formatINR(eligibleTotal)}  accent="#f59e0b" />
      <QCard icon={<FiDollarSign size={14} />}    label="Total Incentive"    value={formatINR(totalIncentive)} accent="#0ea5e9" />
    </div>
  );
};

// Coordinator name opens the full drill-down table (DashboardDataView).
const CoordinatorNameCell = ({ coordinator, onOpen }) => (
  <td>
    {onOpen ? (
      <button type="button" className="home-link" onClick={(e) => { e.stopPropagation(); onOpen(coordinator); }}>
        {coordinator.name}
      </button>
    ) : coordinator.name}
  </td>
);

const CoordinatorFullTable = ({ coordinators, footerLabel, onOpenCoordinator }) => {
  const footerTotal = coordinators.reduce((sum, c) => sum + c.total, 0);

  return (
    <table className="vendor-table">
      <thead>
        <tr>
          <th>Coordinator</th>
          <th>Won</th>
          <th>Eligible</th>
          <th>Overseas</th>
          <th>India</th>
          <th>Manmonth</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        {coordinators.length === 0 && (
          <tr><td colSpan={7} className="home-muted">No won enquiries this month.</td></tr>
        )}
        {coordinators.map((c) => (
          <tr key={c.coordinatorId} className="home-row-clickable" onClick={() => onOpenCoordinator?.(c)}>
            <CoordinatorNameCell coordinator={c} onOpen={onOpenCoordinator} />
            <td>{c.wonCount}</td>
            <td>
              {c.eligible ? (
                <span className="home-pill home-pill-good">Yes</span>
              ) : (
                <span className="home-pill home-pill-warn">No</span>
              )}
            </td>
            <td>{formatINR(c.breakdown.others.payout)}</td>
            <td>{formatINR(c.breakdown.india.payout)}</td>
            <td>{formatINR(c.breakdown.manmonthOthers.payout + c.breakdown.manmonthIndia.payout)}</td>
            <td className="home-total-cell">{formatINR(c.total)}</td>
          </tr>
        ))}
      </tbody>
      {coordinators.length > 0 && (
        <tfoot>
          <tr>
            <td colSpan={6}>{footerLabel}</td>
            <td className="home-total-cell">{formatINR(footerTotal)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
};

// ── Quarterly incentive (financial-year quarters, Q1 = Apr–Jun) ──────────────
// Keys match the backend: 'FY2026-Q3' = Oct–Dec 2026 of FY 2026-27.
const QUARTER_MONTHS = { 1: 'Apr–Jun', 2: 'Jul–Sep', 3: 'Oct–Dec', 4: 'Jan–Mar' };

const quarterKeyForDate = (date) => {
  const m = date.getMonth();
  const fyStart = m >= 3 ? date.getFullYear() : date.getFullYear() - 1;
  return `FY${fyStart}-Q${Math.floor(((m - 3 + 12) % 12) / 3) + 1}`;
};

const formatQuarterLabel = (key) => {
  const match = /^FY(\d{4})-Q([1-4])$/.exec(key || '');
  if (!match) return '';
  const fy = Number(match[1]);
  return `Q${match[2]} FY ${fy}-${String(fy + 1).slice(2)} (${QUARTER_MONTHS[match[2]]})`;
};

// Current quarter plus the previous seven, newest first.
const recentQuarterKeys = () => {
  const keys = [];
  const d = new Date();
  d.setDate(1);
  for (let i = 0; i < 8; i += 1) {
    keys.push(quarterKeyForDate(d));
    d.setMonth(d.getMonth() - 3);
  }
  return keys;
};

// The three 'YYYY-MM' months of a quarter key, in order.
const quarterMonthKeys = (key) => {
  const match = /^FY(\d{4})-Q([1-4])$/.exec(key || '');
  if (!match) return [];
  const startMonthIndex = 3 + (Number(match[2]) - 1) * 3;
  return [0, 1, 2].map((i) => {
    const d = new Date(Number(match[1]), startMonthIndex + i, 1);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  });
};

const formatMonthKey = (key) => {
  const [y, m] = key.split('-').map(Number);
  return new Date(y, m - 1, 1).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
};

const formatLakh = (n) => `₹${(Number(n || 0) / 100000).toLocaleString('en-IN', { maximumFractionDigits: 2 })}L`;

// Progress to target split in two: blue = invoiced (payable), red = probable
// enquiry value not invoiced yet. Both are measured against the same target,
// and together they're capped at a full bar.
const SplitQuarterProgress = ({ invoiced, probable, minRevenue }) => {
  const notInvoiced = Math.max(0, (probable || 0) - (invoiced || 0));
  const pctOf = (n) => (minRevenue > 0 ? (n / minRevenue) * 100 : 0);
  const bluePct = Math.min(100, pctOf(invoiced));
  const redPct = Math.min(100 - bluePct, pctOf(notInvoiced));
  return (
    <div>
      <div
        className="home-q-progress"
        style={{ display: 'flex' }}
        title={`Invoiced ${formatINR(invoiced)} + not invoiced ${formatINR(notInvoiced)} of ${formatINR(minRevenue)} target`}
      >
        <div className="home-q-split home-q-split--invoiced" style={{ width: `${bluePct}%` }} />
        <div className="home-q-split home-q-split--pending" style={{ width: `${redPct}%` }} />
      </div>
      <div className="home-q-legend">
        <span><i className="home-q-dot home-q-split--invoiced" />Invoiced {formatLakh(invoiced)}</span>
        <span><i className="home-q-dot home-q-split--pending" />Not invoiced {formatLakh(notInvoiced)}</span>
      </div>
    </div>
  );
};

const SlabBreakdown =({ slabs, eligible }) => (
  <table className="vendor-table">
    <thead>
      <tr>
        <th>Invoiced revenue band</th>
        <th>Rate</th>
        <th>Invoiced revenue in band</th>
        <th>{eligible ? 'Payout' : 'Payout (once target met)'}</th>
      </tr>
    </thead>
    <tbody>
      {slabs.map((s) => (
        <tr key={s.min}>
          <td>{formatLakh(s.min)} – {s.max == null ? 'above' : formatLakh(s.max)}</td>
          <td>{s.ratePercent}%</td>
          <td>{formatINR(Math.round(s.amountInSlab))}</td>
          {/* backend zeroes `payout` below target; show what the band would pay */}
          <td>{formatINR(Math.round((s.amountInSlab * s.ratePercent) / 100))}</td>
        </tr>
      ))}
    </tbody>
  </table>
);

// Five-card summary row for the quarterly incentive section — shown for both
// the per-coordinator admin table and the coordinator's own self-view.
const QCard = ({ icon, label, value, accent }) => (
  <div className="home-summary-card" style={{ border: `1.5px solid ${accent}`, background: '#fff', flex: '1 1 160px' }}>
    <div className="home-summary-label" style={{ color: accent }}>{icon}{label}</div>
    <div className="home-summary-value">{value}</div>
  </div>
);
const QuarterSummaryCards = ({ totalEnquiries, probableRevenue, probableIncentive, invoicedRevenue, actualIncentive }) => (
  <div className="home-summary-row" style={{ marginBottom: 16 }}>
    <QCard icon={<FiClipboard size={14} />} label="Total Enquiries"    value={totalEnquiries ?? '—'}       accent="#6366f1" />
    <QCard icon={<FiTrendingUp size={14} />} label="Probable Revenue"  value={formatINR(probableRevenue)}  accent="#8b5cf6" />
    <QCard icon={<FiAward size={14} />}      label="Probable Incentive" value={formatINR(probableIncentive)} accent="#f59e0b" />
    <QCard icon={<FiDollarSign size={14} />} label="Invoiced Revenue"  value={formatINR(invoicedRevenue)}  accent="#0ea5e9" />
    <QCard icon={<FiCheckCircle size={14} />} label="Actual Incentive" value={formatINR(actualIncentive)}  accent="#22c55e" />
  </div>
);


const QuarterlySelfView = ({ data, viewDetailsUrl }) => {
  if (data.enabled === false) {
    return <p className="home-muted">The quarterly incentive is not enabled for your company.</p>;
  }
  return (
    <div>
      <QuarterSummaryCards
        totalEnquiries={data.enquiryCount ?? '—'}
        probableRevenue={data.probable?.revenue ?? 0}
        probableIncentive={data.probable?.total ?? 0}
        invoicedRevenue={data.revenue ?? 0}
        actualIncentive={data.total ?? 0}
      />
      <div className="home-self-sub" style={{ fontWeight: 600, marginBottom: 4 }}>Progress to target</div>
      <SplitQuarterProgress invoiced={data.revenue} probable={data.probable?.revenue} minRevenue={data.minRevenue} />
      {viewDetailsUrl && (
        <a href={viewDetailsUrl} className="home-link" style={{ display: 'inline-block', marginTop: 12, fontSize: 13 }}>
          View details →
        </a>
      )}
    </div>
  );
};

const QuarterlyFullTable = ({ coordinators, onOpenCoordinator }) => {
  const [expandedId, setExpandedId] = useState(null);
  const footerTotal = coordinators.reduce((sum, c) => sum + c.total, 0);
  const footerProbable = coordinators.reduce((sum, c) => sum + (c.probable?.total || 0), 0);

  return (
    <table className="vendor-table">
      <thead>
        <tr>
          <th>Coordinator</th>
          <th>Probable revenue</th>
          <th>Probable incentive</th>
          <th>Invoiced revenue</th>
          <th style={{ width: '18%' }}>Progress to target</th>
          <th>Eligible</th>
          <th>Actual incentive</th>
        </tr>
      </thead>
      <tbody>
        {coordinators.length === 0 && (
          <tr><td colSpan={7} className="home-muted">No won inspection days or issued invoices this quarter.</td></tr>
        )}
        {coordinators.map((c) => {
          const isOpen = expandedId === c.coordinatorId;
          return (
            <React.Fragment key={c.coordinatorId}>
              <tr className="home-row-clickable" onClick={() => setExpandedId(isOpen ? null : c.coordinatorId)}>
                <CoordinatorNameCell coordinator={c} onOpen={onOpenCoordinator} />
                <td>{formatINR(c.probable?.revenue || 0)}</td>
                <td title={c.probable?.eligible ? '' : `Would pay ${formatINR(c.probable?.potentialTotal || 0)} once target met`}>
                  {formatINR(c.probable?.total || 0)}
                </td>
                <td>{formatINR(c.revenue)}</td>
                <td><SplitQuarterProgress invoiced={c.revenue} probable={c.probable?.revenue} minRevenue={c.minRevenue} /></td>
                <td>
                  {c.eligible
                    ? <span className="home-pill home-pill-good">Yes</span>
                    : <span className="home-pill home-pill-warn">No - {formatLakh(c.shortfall)} short</span>}
                </td>
                <td className="home-total-cell">{formatINR(c.total)}</td>
              </tr>
              {isOpen && (
                <tr>
                  <td colSpan={7} className="home-detail-cell">
                    <SlabBreakdown slabs={c.slabs} eligible={c.eligible} />
                  </td>
                </tr>
              )}
            </React.Fragment>
          );
        })}
      </tbody>
      {coordinators.length > 0 && (
        <tfoot>
          <tr>
            <td colSpan={2}>Quarter total</td>
            <td>{formatINR(footerProbable)}</td>
            <td colSpan={3} />
            <td className="home-total-cell">{formatINR(footerTotal)}</td>
          </tr>
        </tfoot>
      )}
    </table>
  );
};

const QuarterlyIncentiveSection = ({ isGlobalAdmin, seesAllCoordinators, allCoordinatorsLabel = 'all coordinators', selectedCompany, coordFilter, externalQuarter, externalMonth, onOpenCoordinator, coordinatorDetailsUrl }) => {
  const [quarterOptions] = useState(recentQuarterKeys);
  const [internalQuarter, setInternalQuarter] = useState(quarterOptions[0]);
  const quarter = externalQuarter || internalQuarter;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      setLoading(true);
      const token = localStorage.getItem('token');
      const params = new URLSearchParams({ quarter });
      if (externalMonth) params.set('month', externalMonth);
      if (isGlobalAdmin && selectedCompany) params.set('companyId', selectedCompany.id || selectedCompany._id);
      const res = await apiRequest(`${API_URLS.INCENTIVE}/coordinator/quarterly?${params.toString()}`, { method: 'GET' }, token);
      if (cancelled) return;
      setData(res?.ok ? (res.data?.data ?? null) : null);
      setLoading(false);
    };
    load();
    return () => { cancelled = true; };
  }, [quarter, externalMonth, isGlobalAdmin, selectedCompany]);

  const coordinators = (data?.coordinators || []).filter((c) => !coordFilter || c.coordinatorId === coordFilter.id);

  const adminSummary = seesAllCoordinators && data ? {
    totalEnquiries: coordinators.reduce((s, c) => s + (c.enquiries?.length || 0), 0),
    probableRevenue: coordinators.reduce((s, c) => s + (c.probable?.revenue || 0), 0),
    probableIncentive: data.grandProbableTotal || coordinators.reduce((s, c) => s + (c.probable?.total || 0), 0),
    invoicedRevenue: coordinators.reduce((s, c) => s + (c.revenue || 0), 0),
    actualIncentive: data.grandTotal || coordinators.reduce((s, c) => s + (c.total || 0), 0),
  } : null;

  return (
    <section className="home-card">
      <div className="home-q-header">
        <h3>Quarterly incentive{externalMonth ? ` (${formatMonthKey(externalMonth)})` : ''}{coordFilter ? ` - ${coordFilter.name}` : seesAllCoordinators ? ` - ${allCoordinatorsLabel}` : ''}</h3>
        {!externalQuarter && (
          <select className="home-month-picker" value={internalQuarter} onChange={(e) => setInternalQuarter(e.target.value)}>
            {quarterOptions.map((k) => <option key={k} value={k}>{formatQuarterLabel(k)}</option>)}
          </select>
        )}
      </div>
      {loading && <p className="home-muted">Loading&hellip;</p>}
      {!loading && data && seesAllCoordinators && (
        <>
          <QuarterSummaryCards {...adminSummary} />
          <QuarterlyFullTable coordinators={coordinators} onOpenCoordinator={(c) => onOpenCoordinator?.(c, quarter)} />
        </>
      )}
      {!loading && data && !seesAllCoordinators && <QuarterlySelfView data={data} viewDetailsUrl={coordinatorDetailsUrl ? coordinatorDetailsUrl(quarter) : null} />}
      {!loading && !data && <p className="home-muted">No data for this quarter yet.</p>}
    </section>
  );
};


const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isGlobalAdmin = user?.role === 'global_superadmin';
  const isCoordinator = user?.role === 'coordinator';
  // Finance sees every coordinator of their own company (backend scopes it);
  // only global_superadmin can switch company.
  const isFinance = user?.role === 'finance';
  // Project manager gets the same breakdown, scoped by the backend to
  // themselves plus the coordinators reporting to them.
  const isProjectManager = user?.role === 'project_manager';
  const seesAllCoordinators = isGlobalAdmin || isFinance || isProjectManager;
  const allCoordinatorsLabel = isProjectManager ? 'my team' : 'all coordinators';
  const canSeeIncentive = seesAllCoordinators || isCoordinator;

  // Read once on mount — frozen via useState's lazy initializer so later
  // renders don't re-read sessionStorage.
  const [savedFilters] = useState(loadSavedHomeFilters);

  const [month, setMonth] = useState(savedFilters.month || currentMonthKey());
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const [companies, setCompanies] = useState([]);
  const [selectedCompany, setSelectedCompany] = useState(savedFilters.selectedCompany || null);

  const [coordFilter, setCoordFilter] = useState(savedFilters.coordFilter || null);

  const [periodType, setPeriodType] = useState(savedFilters.periodType || 'monthly');
  const [headerQuarter, setHeaderQuarter] = useState(() => savedFilters.headerQuarter || recentQuarterKeys()[0]);
  // '' = whole quarter; otherwise one 'YYYY-MM' month inside headerQuarter.
  const [headerQuarterMonth, setHeaderQuarterMonth] = useState(() =>
    quarterMonthKeys(savedFilters.headerQuarter).includes(savedFilters.headerQuarterMonth) ? savedFilters.headerQuarterMonth : '');

  // Persist every filter change so navigating to a drill-down view (Job Number →
  // dashboardViews) and clicking back restores the same period/company/coordinator
  // instead of resetting to the defaults.
  useEffect(() => {
    try {
      sessionStorage.setItem(HOME_FILTERS_KEY, JSON.stringify({
        periodType, month, headerQuarter, headerQuarterMonth,
        coordFilter: coordFilter ? { id: coordFilter.id, name: coordFilter.name } : null,
        selectedCompany: selectedCompany
          ? { id: selectedCompany.id || selectedCompany._id, companyName: selectedCompany.companyName }
          : null,
      }));
    } catch {
      // sessionStorage unavailable (private mode, quota, etc.) — filters just won't persist.
    }
  }, [periodType, month, headerQuarter, headerQuarterMonth, coordFilter, selectedCompany]);

  useEffect(() => {
    if (!isGlobalAdmin) return;
    const load = async () => {
      const token = localStorage.getItem('token');
      const res = await apiRequest(API_URLS.COMPANIES, { method: 'GET' }, token);
      if (res?.ok) setCompanies(res.data || []);
    };
    load();
  }, [isGlobalAdmin]);

  // Skips the coordFilter reset below on the very first fetch after mount, so a
  // coordinator restored from sessionStorage survives the initial load — it still
  // resets on every later month/company change the user actually makes.
  const didFetchOnceRef = useRef(false);
  const fetchIncentive = useCallback(async () => {
    if (!canSeeIncentive) { setLoading(false); return; }
    setLoading(true);
    if (didFetchOnceRef.current) setCoordFilter(null);
    didFetchOnceRef.current = true;
    const token = localStorage.getItem('token');
    const params = new URLSearchParams({ month });
    if (isGlobalAdmin && selectedCompany) {
      params.set('companyId', selectedCompany.id || selectedCompany._id);
    }
    const res = await apiRequest(`${API_URLS.INCENTIVE}/coordinator?${params.toString()}`, { method: 'GET' }, token);
    if (res?.ok) setData(res.data?.data ?? null);
    setLoading(false);
  }, [canSeeIncentive, month, isGlobalAdmin, selectedCompany]);

  useEffect(() => { fetchIncentive(); }, [fetchIncentive]);


  const companyParam = isGlobalAdmin && selectedCompany ? { companyId: selectedCompany.id || selectedCompany._id } : {};
  const openMonthlyCoordinator = (c) => navigate(`/dashboard/view/coordinator-incentive?${new URLSearchParams({
    coordinatorId: c.coordinatorId, name: c.name, month, ...companyParam,
  })}`);
  const openQuarterlyCoordinator = (c, quarter) => navigate(`/dashboard/view/coordinator-quarterly-incentive?${new URLSearchParams({
    coordinatorId: c.coordinatorId, name: c.name, quarter, ...(headerQuarterMonth ? { month: headerQuarterMonth } : {}), ...companyParam,
  })}`);

  const coordinatorOptions = (data?.coordinators || []).map((c) => ({ id: c.coordinatorId, name: c.name }));
  const visibleCoordinators = coordFilter
    ? (data?.coordinators || []).filter((c) => c.coordinatorId === coordFilter.id)
    : (data?.coordinators || []);

  // A selected coordinator narrows every summary card to just their own
  // numbers; otherwise they stay at the company/global aggregate the
  // backend already computed.
  const EMPTY_COUNTS = { total: 0, won: 0, inProcess: 0, lost: 0 };
  const summaryCounts = seesAllCoordinators && coordFilter
    ? visibleCoordinators.reduce((acc, c) => ({
        total: acc.total + (c.counts?.total || 0),
        won: acc.won + (c.counts?.won || 0),
        inProcess: acc.inProcess + (c.counts?.inProcess || 0),
        lost: acc.lost + (c.counts?.lost || 0),
      }), EMPTY_COUNTS)
    : (data?.counts || EMPTY_COUNTS);
  const eligibleTotal = seesAllCoordinators && coordFilter
    ? visibleCoordinators.reduce((sum, c) => sum + c.potentialTotal, 0)
    : data?.eligibleTotal;
  const totalIncentive = seesAllCoordinators
    ? (coordFilter ? visibleCoordinators.reduce((sum, c) => sum + c.total, 0) : data?.grandTotal)
    : data?.total;

  return (
    <div className="Home">
    <div className="home-page">
      <div className="home-header">
        <div>
          <h2>Home</h2>
          <p className="home-welcome">Welcome, {user?.fullName || 'there'}.</p>
        </div>

        <div className="home-controls">
          <Select
            options={[
              { value: 'monthly', label: 'Monthly' },
              { value: 'quarterly', label: 'Quarterly' },
            ]}
            value={{ value: periodType, label: periodType === 'monthly' ? 'Monthly' : 'Quarterly' }}
            onChange={opt => setPeriodType(opt.value)}
            styles={{ ...filterSelectStyles, container: (b) => ({ ...b, width: 130 }) }}
            isSearchable={false}
            isClearable={false}
          />
          {periodType === 'monthly' && (
            <input
              type="month"
              className="home-month-picker"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
            />
          )}
          {periodType === 'quarterly' && (
            <Select
              options={recentQuarterKeys().map(k => ({ value: k, label: formatQuarterLabel(k) }))}
              value={{ value: headerQuarter, label: formatQuarterLabel(headerQuarter) }}
              onChange={opt => { setHeaderQuarter(opt.value); setHeaderQuarterMonth(''); }}
              styles={{ ...filterSelectStyles, container: (b) => ({ ...b, width: 225 }) }}
              isSearchable={false}
              isClearable={false}
            />
          )}
          {periodType === 'quarterly' && (
            <Select
              options={[
                { value: '', label: 'All months' },
                ...quarterMonthKeys(headerQuarter).map(k => ({ value: k, label: formatMonthKey(k) })),
              ]}
              value={{ value: headerQuarterMonth, label: headerQuarterMonth ? formatMonthKey(headerQuarterMonth) : 'All months' }}
              onChange={opt => setHeaderQuarterMonth(opt.value)}
              styles={{ ...filterSelectStyles, container: (b) => ({ ...b, width: 170 }) }}
              isSearchable={false}
              isClearable={false}
            />
          )}
          {(seesAllCoordinators || isGlobalAdmin) && (
            <FilterPanel
              values={{
                coordinator: coordFilter?.id || '',
                company: selectedCompany?.id || selectedCompany?._id || '',
              }}
              emptyValues={{ coordinator: '', company: '' }}
              onChange={(v) => {
                const newCoord = coordinatorOptions.find(c => c.id === v.coordinator) || null;
                setCoordFilter(newCoord);
                const newCompany = companies.find(c => (c.id || c._id) === v.company) || null;
                setSelectedCompany(newCompany);
              }}
              title="Filters"
              width={240}
            >
              {(filters, set) => (
                <>
                  {seesAllCoordinators && (
                    <FilterField label="Coordinator">
                      <Select
                        options={[
                          { value: '', label: 'All Coordinators' },
                          ...coordinatorOptions.map(c => ({ value: c.id, label: c.name })),
                        ]}
                        value={{ value: filters.coordinator, label: coordinatorOptions.find(c => c.id === filters.coordinator)?.name || 'All Coordinators' }}
                        onChange={opt => set({ coordinator: opt?.value || '' })}
                        styles={filterSelectStyles}
                        isClearable={false}
                        placeholder="All Coordinators"
                      />
                    </FilterField>
                  )}
                  {isGlobalAdmin && (
                    <FilterField label="Company">
                      <Select
                        options={[
                          { value: '', label: 'Global' },
                          ...companies.map(c => ({ value: c.id || c._id, label: c.companyName })),
                        ]}
                        value={{ value: filters.company, label: companies.find(c => (c.id || c._id) === filters.company)?.companyName || 'Global' }}
                        onChange={opt => set({ company: opt?.value || '' })}
                        styles={filterSelectStyles}
                        isClearable={false}
                        placeholder="Global"
                      />
                    </FilterField>
                  )}
                </>
              )}
            </FilterPanel>
          )}
        </div>
      </div>

      {periodType === 'monthly' && !loading && data && (
        <HomeSummaryCards counts={summaryCounts} eligibleTotal={eligibleTotal} totalIncentive={totalIncentive} />
      )}

      {periodType === 'monthly' && canSeeIncentive && (
        <section className="home-card">
          <h3>Coordinator incentive{coordFilter ? ` - ${coordFilter.name}` : seesAllCoordinators ? ` - ${allCoordinatorsLabel}` : ''}</h3>

          {loading && <p className="home-muted">Loading&hellip;</p>}
          {!loading && data && (seesAllCoordinators
            ? <CoordinatorFullTable coordinators={visibleCoordinators} footerLabel={coordFilter ? 'Total' : isProjectManager ? 'Team total' : 'Company total'} onOpenCoordinator={openMonthlyCoordinator} />
            : <SimpleIncentiveView data={data} viewDetailsUrl={isCoordinator && data.details ? `/dashboard/view/coordinator-incentive?${new URLSearchParams({ coordinatorId: user._id, name: user.fullName || '', month })}` : null} />)}
          {!loading && !data && <p className="home-muted">No data for this month yet.</p>}
        </section>
      )}

      {periodType === 'quarterly' && canSeeIncentive && (
        <QuarterlyIncentiveSection
          isGlobalAdmin={isGlobalAdmin}
          seesAllCoordinators={seesAllCoordinators}
          allCoordinatorsLabel={allCoordinatorsLabel}
          selectedCompany={selectedCompany}
          coordFilter={coordFilter}
          externalQuarter={headerQuarter}
          externalMonth={headerQuarterMonth}
          onOpenCoordinator={openQuarterlyCoordinator}
          coordinatorDetailsUrl={isCoordinator ? (q) => `/dashboard/view/coordinator-quarterly-incentive?${new URLSearchParams({ coordinatorId: user._id, name: user.fullName || '', quarter: q, ...(headerQuarterMonth ? { month: headerQuarterMonth } : {}) })}` : null}
        />
      )}
    </div>
  </div>
  );
};

export default Home;
